import { Tag, Tooltip, type TableColumnsType } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import React from 'react';
import styles from './Styles.module.scss';
import cs from 'classnames';
import type { DiagnosisItem, Patient } from '@packages/types';
import { DiagnosisListCell } from '@ui/components/common';

export const patientColumns: TableColumnsType<Patient> = [
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 100,
        onHeaderCell: () =>
            ({
                'data-testid': 'patients-table-header-name',
            }) as React.HTMLAttributes<HTMLTableCellElement>,
        render: (name, record) => (
            <div>
                <div className="font-semibold text-[#262626]">{name}</div>
                <div className="flex flex-col gap-0.5 text-[11px] text-[#8c8c8c]">
                    {(() => {
                        const isEsOnly = (record as Patient & { isEsOnly?: boolean }).isEsOnly === true;
                        return isEsOnly ? (
                            <span>ID: {record.sourcePatientId ?? record.id}</span>
                        ) : (
                            <span>OMOP ID: {record.omopPersonId ?? record.id}</span>
                        );
                    })()}
                </div>
            </div>
        ),
    },

    {
        title: 'Source',
        dataIndex: 'sourceFlags',
        key: 'source',
        width: 100,
        onHeaderCell: () =>
            ({
                'data-testid': 'patients-table-header-source',
            }) as React.HTMLAttributes<HTMLTableCellElement>,
        render: (_: unknown, record: Patient) => {
            const sourceFlags = record.sourceFlags;
            const isSilver = sourceFlags?.includes('omop');
            const isBronze = sourceFlags?.includes('es') || sourceFlags?.includes('es-only');
            return sourceFlags?.length ? (
                <span className="flex gap-0.5">
                    {isSilver && <Tag className={cs('!m-0 !text-[10px]', styles.silverTag)}>Silver</Tag>}
                    {isBronze && <Tag className={cs('!m-0 !text-[10px]', styles.bronzeTag)}>Bronze</Tag>}
                </span>
            ) : (
                <span className="text-[#8c8c8c]">-</span>
            );
        },
    },

    {
        title: 'Documents',
        dataIndex: 'documentCount',
        key: 'documentCount',
        width: 120,
        render: count => count || 0,
        onHeaderCell: () =>
            ({
                'data-testid': 'patients-table-header-documents',
            }) as React.HTMLAttributes<HTMLTableCellElement>,
    },

    {
        title: 'Age',
        dataIndex: 'age',
        key: 'age',
        width: 100,
        onHeaderCell: () =>
            ({
                'data-testid': 'patients-table-header-age',
            }) as React.HTMLAttributes<HTMLTableCellElement>,
    },

    {
        title: 'Gender',
        dataIndex: 'gender',
        key: 'gender',
        width: 100,
        onHeaderCell: () =>
            ({
                'data-testid': 'patients-table-header-gender',
            }) as React.HTMLAttributes<HTMLTableCellElement>,
        filterIcon: (filtered: boolean) => (
            <span data-testid="patients-table-filter-icon-gender">
                <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            </span>
        ),
        render: gender => {
            const genderLower = gender?.toLowerCase();
            return (
                <Tag
                    className={cs(styles.genderTag, {
                        [styles.maleTag]: genderLower === 'male',
                        [styles.femaleTag]: genderLower === 'female',
                    })}
                >
                    {gender}
                </Tag>
            );
        },
    },

    {
        title: 'Race',
        dataIndex: 'race',
        key: 'race',
        width: 120,
        onHeaderCell: () =>
            ({
                'data-testid': 'patients-table-header-race',
            }) as React.HTMLAttributes<HTMLTableCellElement>,
        filterIcon: (filtered: boolean) => (
            <span data-testid="patients-table-filter-icon-race">
                <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            </span>
        ),
        render: race => race || '-',
    },

    {
        title: 'Ethnicity',
        dataIndex: 'ethnicity',
        key: 'ethnicity',
        width: 120,
        onHeaderCell: () =>
            ({
                'data-testid': 'patients-table-header-ethnicity',
            }) as React.HTMLAttributes<HTMLTableCellElement>,
        filterIcon: (filtered: boolean) => (
            <span data-testid="patients-table-filter-icon-ethnicity">
                <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            </span>
        ),
        render: ethnicity => ethnicity || '-',
    },

    {
        title: 'Diagnoses',
        dataIndex: 'diagnosis_list',
        key: 'diagnosis_list',
        width: 200,
        onHeaderCell: () =>
            ({
                'data-testid': 'patients-table-header-diagnoses',
            }) as React.HTMLAttributes<HTMLTableCellElement>,
        render: (diagnoses: DiagnosisItem[]) => (
            <DiagnosisListCell
                diagnoses={diagnoses || []}
                maxDisplay={2}
            />
        ),
    },

    {
        title: 'Diagnosis',
        dataIndex: 'diagnosis',
        key: 'diagnosis',
        width: 250,
        ellipsis: true,
        onHeaderCell: () =>
            ({
                'data-testid': 'patients-table-header-diagnosis',
            }) as React.HTMLAttributes<HTMLTableCellElement>,
        filterIcon: (filtered: boolean) => (
            <span data-testid="patients-table-filter-icon-diagnosis">
                <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            </span>
        ),
        render: (diagnosis: string[]) => {
            if (!diagnosis || diagnosis.length === 0) return '-';

            const visible = diagnosis.slice(0, 2);
            const remaining = diagnosis.length - 2;

            const tooltipContent = (
                <div className="flex max-h-[200px] flex-wrap gap-[4px] overflow-auto text-[#4D4D4D]">
                    {diagnosis.map((d, i) => (
                        <Tag
                            key={i}
                            className="!mr-0"
                        >
                            {d}
                        </Tag>
                    ))}
                </div>
            );

            return (
                <Tooltip
                    title={tooltipContent}
                    placement="topLeft"
                    color="white"
                    styles={{ body: { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } }}
                >
                    <div className="flex flex-wrap items-center gap-[4px]">
                        {visible.map((d, i) => (
                            <Tag
                                key={i}
                                className="!mr-0 max-w-[100px] truncate"
                            >
                                {d}
                            </Tag>
                        ))}
                        {remaining > 0 && <span className="text-xs text-gray-400">+{remaining}</span>}
                    </div>
                </Tooltip>
            );
        },
    },
];
